import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://lawyer-marwahawshar.org';

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (link) link.setAttribute('href', href);
}

// Overrides the document title/description/keywords/canonical for the
// lifetime of the calling page, restoring the site defaults on unmount.
// Extends the same manual meta-tag pattern already used in src/i18n.js.
//
// `alternates` (optional): [{ hreflang: 'en'|'ar', path: '/...' }] — used only
// by the standalone English expat pages and their Arabic counterparts, which
// are the one part of the site that uses real per-language URLs instead of
// the site-wide AR/EN toggle (see ExpatEnPage). Tags are created fresh and
// removed on unmount rather than restored, since the default document never
// ships any hreflang tags of its own.
export default function usePageMeta({ title, description, keywords, path, alternates }) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const previousTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const previousDescription = metaDesc ? metaDesc.getAttribute('content') : null;
    const previousKeywords = metaKeywords ? metaKeywords.getAttribute('content') : null;
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalLink ? canonicalLink.getAttribute('href') : null;
    const addedAlternateLinks = [];

    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (keywords && keywords.length) {
      setMeta('meta[name="keywords"]', 'content', keywords.join(', '));
    }
    if (path) {
      const url = `${SITE_URL}${path}`;
      setCanonical(url);
      setMeta('meta[property="og:url"]', 'content', url);
    }
    if (Array.isArray(alternates)) {
      alternates.forEach(({ hreflang, path: altPath }) => {
        if (!hreflang || !altPath) return;
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', hreflang);
        link.setAttribute('href', `${SITE_URL}${altPath}`);
        document.head.appendChild(link);
        addedAlternateLinks.push(link);
      });
    }

    return () => {
      document.title = previousTitle;
      if (metaDesc && previousDescription !== null) metaDesc.setAttribute('content', previousDescription);
      if (metaKeywords && previousKeywords !== null) metaKeywords.setAttribute('content', previousKeywords);
      if (canonicalLink && previousCanonical !== null) canonicalLink.setAttribute('href', previousCanonical);
      addedAlternateLinks.forEach((link) => link.remove());
    };
  }, [title, description, keywords, path, alternates, i18n.language]);
}
